import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { login, googleAuth } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useTutorialVideos } from '../contexts/TutorialVideoContext';
import GoogleAuthButton from './GoogleAuthButton';
import AuthLayout from './AuthLayout';

function LoginForm({ onLogin }) {
  const location = useLocation();
  const { refreshProfile } = useUser();
  const { refreshVideos } = useTutorialVideos();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(location.state?.message || '');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear previous errors

    // Validate inputs
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setIsLoading(true);
    const result = await login(username, password);
    setIsLoading(false);

    if (result.token) {
      await refreshProfile(); // Fetch user profile for navbar
      refreshVideos(); // Fetch tutorial videos for walkthrough buttons
      onLogin(); // update App state
      navigate('/watchlist'); // redirect
    } else {
      setError(result.error || 'Login failed. Please check your credentials and try again.');
    }
  };

  const handleGoogleSuccess = async (credential) => {
    setError('');
    setIsLoading(true);

    const result = await googleAuth(credential);
    setIsLoading(false);

    if (result.token) {
      await refreshProfile(); // Fetch user profile for navbar
      refreshVideos(); // Fetch tutorial videos for walkthrough buttons
      onLogin(); // update App state
      navigate('/watchlist'); // redirect
    } else {
      setError(result.error || 'Google authentication failed. Please try again.');
    }
  };

  const handleGoogleError = (error) => {
    setError(error || 'Google authentication failed. Please try again.');
  };

  return (
    <AuthLayout>
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-surface rounded-lg shadow-lg p-6 space-y-5 border border-muted"
      >
        <h2 className="text-2xl font-bold text-brand-white text-center">Login</h2>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-900/20 border border-green-500/50 text-green-300 px-4 py-3 rounded text-sm">
            {successMessage}
          </div>
        )}

        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-brand-gray-blue rounded focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-brand-white placeholder-gray-400"
          disabled={isLoading}
          required
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full p-3 bg-gray-800 border border-brand-gray-blue rounded focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue text-brand-white placeholder-gray-400"
          disabled={isLoading}
          required
        />

        <div className="text-right">
          <a
            href="/request-password-reset"
            className="text-sm text-brand-blue hover:text-[#1a7aef] transition-colors"
          >
            Forgot password?
          </a>
        </div>

        <button
          type="submit"
          className="w-full bg-brand-blue text-white font-semibold py-3 rounded-lg hover:bg-[#1a7aef] transition-colors disabled:bg-brand-blue/50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? 'Logging in...' : 'Login'}
        </button>

        <div className="relative flex items-center justify-center my-4">
          <div className="border-t border-muted w-full"></div>
          <span className="px-3 text-sm text-brand-gray-blue bg-surface absolute">or</span>
        </div>

        <GoogleAuthButton
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
        />

        <div className="border-t border-muted pt-4 mt-4">
          <p className="text-center text-brand-gray-blue text-sm mb-3">
            New to OptionsLeveling?
          </p>
          <a
            href="/signup"
            className="block w-full text-center bg-green-400 text-black font-semibold py-3 rounded-lg hover:bg-green-500 transition-colors"
          >
            Create an Account
          </a>
        </div>

        <p className="text-center text-brand-gray-blue text-xs mt-4">
          By logging in, you agree to our{' '}
          <a href="/terms" className="text-brand-blue hover:text-[#1a7aef] underline transition-colors">
            Terms of Use
          </a>
          {' '}and{' '}
          <a href="/privacy" className="text-brand-blue hover:text-[#1a7aef] underline transition-colors">
            Privacy Policy
          </a>
        </p>
      </form>
    </AuthLayout>
  );
}

export default LoginForm;
